import React from "react";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import SEOHead from "../components/common/SEOHead";

const PrivacyPolicy = () => {
  const seoData = {
    title: "Privacy Policy - Livneu | Data Protection & User Privacy",
    description:
      "Read Livneu's comprehensive privacy policy. Learn how we collect, use, and protect your personal information in accordance with Sri Lankan and international privacy laws.",
    image: "https://livneu.levelstair.com/ShareImage.png", 
    url: "https://livneu.levelstair.com/privacy-policy",
    keywords:
      "privacy policy, data protection, user privacy, Livneu privacy, LEVELSTAIR privacy, personal information protection",
  };

  return (
    <HelmetProvider>
      <div className="min-h-screen">
        <SEOHead seoData={seoData} />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-gradient-privacy">
          {/* Kawaii Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-pastel-purple/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-pastel-blue/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "2s" }}></div>
            <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-kawaii-mint/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: "4s" }}></div>
          </div>

          <div className="container mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-7xl font-title text-text-accent mb-6 leading-tight kawaii-glow">
                Privacy Policy
              </h1>
              <motion.p
                className="text-xl md:text-2xl text-text-secondary mb-8 leading-relaxed font-body"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Your privacy is important to us. This policy explains how Livneu collects, 
                uses, and protects your personal information.
              </motion.p>
              <p className="text-sm text-text-secondary/60">
                Last updated: January 2025
              </p>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="glass-card backdrop-blur-sm rounded-2xl p-8 md:p-12 border-pastel-purple/20">
            
            {/* Introduction */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-lg text-text-secondary leading-relaxed mb-6 font-body">
                LEVELSTAIR (Pvt) Ltd., operating Livneu (referred to as "we," "us," "our," or "the Company"), 
                values the privacy of users of our website and services (referred to as "User" or "you"). This Privacy 
                Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
                or use our services.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                By using our services, you consent to the data practices described in this policy. This policy applies 
                to information collected through our website, mobile applications, and related services.
              </p>
            </motion.section>

            {/* Scope of Personal Information */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-purple-400 mb-6">Scope of Personal Information</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                We identify personal information as any information that can be used to identify individual users, 
                including but not limited to:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <span className="text-purple-400 mr-3">1)</span>
                  <span className="text-gray-700">Information provided in registration forms for membership, talent applications, auditions, event registrations, merchandise purchases, and other related forms.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-400 mr-3">2)</span>
                  <span className="text-gray-700">Contact information including email addresses, phone numbers, and social media handles.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-400 mr-3">3)</span>
                  <span className="text-gray-700">Profile information, preferences, and other data you provide when interacting with our services.</span>
                </li>
              </ul>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                We do not share personal information with third parties, except when we have established confidentiality 
                agreements with external service providers for specific business purposes. Users are informed of such 
                arrangements before information is collected.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                This Privacy Policy does not apply to external websites that link to our services. We are not responsible 
                for data practices of these external sites. Please consult the privacy policies of respective websites.
              </p>
            </motion.section>

            {/* Collection of Information */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-purple-400 mb-6">Collection of Information</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                In addition to personal information, we may collect other relevant information to improve our services 
                for user enjoyment and convenience:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">We use advanced tracking technologies for character control functionality in virtual streaming applications. This data is used solely for character control and is not saved or shared with third parties.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">We may request personal information for contests, surveys, auditions, and promotional events.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">When users access our services, we monitor service usage patterns and user behavior for improvement purposes.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">We automatically collect IP addresses, browser information, device information, and cookie data when you browse our services.</span>
                </li>
              </ul>
            </motion.section>

            {/* Use and Disclosure */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-purple-400 mb-6">Use and Disclosure of Information</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                We use personal information collected through our services for the following purposes:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">To provide personalized services, content, and advertisements based on user preferences and behavior.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">To develop new services and improve existing ones based on user feedback and interests.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">To deliver products, prizes, and fulfillment of contest winnings.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">To provide necessary notifications for service operations, updates, and important announcements.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">For statistical analysis and service improvement (anonymized data only).</span>
                </li>
              </ul>
              
              <h3 className="text-2xl font-bold text-black mb-4">Information Disclosure</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                We will not disclose your personal information to third parties, except in the following circumstances:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-purple-400 mr-3">1)</span>
                  <span className="text-gray-700">When you have provided explicit consent for information sharing.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-400 mr-3">2)</span>
                  <span className="text-gray-700">When a service provider requires information to deliver services on our behalf (e.g., shipping address for merchandise delivery). Such providers are bound by confidentiality agreements.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-400 mr-3">3)</span>
                  <span className="text-gray-700">When required by law or legal processes from courts, law enforcement, or other government agencies.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-400 mr-3">4)</span>
                  <span className="text-gray-700">When necessary to protect our rights, property, services, or in cases of immediate danger to human life or safety.</span>
                </li>
              </ul>
            </motion.section>

            {/* Cookies */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-purple-400 mb-6">Use and Collection of Cookies</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Our services use cookies to enhance user experience and improve website functionality. Cookies are small 
                files sent between your browser and our server during website visits.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Cookies help our server identify which pages a particular device has visited, but cannot identify 
                individual users unless personal information is separately provided.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Users can modify browser settings to refuse cookies or display warnings when cookies are received. 
                However, refusing cookies may limit access to certain service features.
              </p>
              
              <h3 className="text-2xl font-bold text-black mb-4">Cookie Usage</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">Detect and prevent unauthorized service use</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">Identify registered users and provide personalized experiences</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">Customize advertisements based on user preferences</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">Measure user engagement and perform statistical analysis</span>
                </li>
              </ul>
            </motion.section>

            {/* Access Logs */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-purple-400 mb-6">Access Logs</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                We record and store access log information on our servers for a specified period when users access our 
                website. Access logs include:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">Date and time of access</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">IP address and location data</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">Browser type and version</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">Pages visited and referring URLs</span>
                </li>
              </ul>
              <p className="text-lg text-gray-700 leading-relaxed">
                This information is used for server operation, accessibility improvement, security management, 
                and investigating unauthorized usage.
              </p>
            </motion.section>

            {/* User Rights */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-purple-400 mb-6">User Rights and Information Management</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Users have the following rights regarding their personal information:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">Access and review personal information we have collected</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">Update or correct inaccurate personal information</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">Request deletion of personal information (subject to legal requirements)</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">Opt-out of marketing communications and data processing for promotional purposes</span>
                </li>
              </ul>
              <p className="text-lg text-gray-700 leading-relaxed">
                To exercise these rights or if you cannot make changes yourself, please contact us using the information 
                provided at the end of this policy.
              </p>
            </motion.section>

            {/* Data Security */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-purple-400 mb-6">Data Security</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                We implement appropriate technical and organizational security measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">SSL encryption for data transmission</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">Secure server infrastructure and regular security updates</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">Limited access to personal information on a need-to-know basis</span>
                </li>
                <li className="flex items-start">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-gray-700">Regular security audits and compliance monitoring</span>
                </li>
              </ul>
            </motion.section>

            {/* Policy Updates */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-purple-400 mb-6">Policy Updates</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                We reserve the right to update this Privacy Policy without prior notice to users. Updated policies 
                apply to all users regardless of when they registered for our services.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Policy revisions will be announced through our website, email notifications, or other appropriate 
                communication channels. We encourage users to review this policy periodically for any changes.
              </p>
            </motion.section>

            {/* Contact Information */}
            <motion.section 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-purple-400 mb-6">Contact Information</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                If you have questions about this Privacy Policy or wish to exercise your rights regarding personal 
                information, please contact us:
              </p>
              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                <h3 className="text-xl font-bold text-black mb-4">Livneu - Privacy Department</h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Company:</strong> LEVELSTAIR (Pvt) Ltd.</p>
                  <p><strong>Email:</strong> levelstair@gmail.com</p>
                  <p><strong>Address:</strong> Colombo, Sri Lanka</p>
                  <p><strong>Response Time:</strong> We aim to respond to privacy inquiries within 7-14 business days.</p>
                </div>
              </div>
            </motion.section>

            </div>
          </div>
        </section>
      </div>
    </HelmetProvider>
  );
};

export default PrivacyPolicy;
